/**
 * Picker Primitives — centralized building blocks for all picker/list UIs.
 *
 * Usage:
 *   <PickerList>
 *     <PickerSearch value={q} onChange={setQ} placeholder="Search…" />
 *     <PickerHeader>Section title</PickerHeader>
 *     <PickerItem icon={<Icon />} selected onClick={…}>Label</PickerItem>
 *     <PickerSeparator />
 *     <PickerItem variant="destructive" icon={<X />} onClick={…}>Clear</PickerItem>
 *   </PickerList>
 *
 * Design principles:
 *   - Trailing checkmark (right) for selected state
 *   - bg-primary/10 highlight for selected items
 *   - Consistent 44px touch targets (py-2.5) on mobile, py-2 on desktop
 *   - No empty left gap — content starts from the left edge (after px-3)
 *   - Icons leading, label flex-1, checkmark + badge trailing
 */

import {
  forwardRef,
  type ReactNode,
  type RefObject,
  type KeyboardEvent,
  type ButtonHTMLAttributes,
} from "react"
import { cn } from "@/shared/utils/general/cn"
import SearchIcon from "@/shared/icons/pikaicons/stroke/general/search-default.svg?react"
import CheckmarkIcon from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"

// ---------------------------------------------------------------------------
// PickerList — scrollable container
// ---------------------------------------------------------------------------

interface PickerListProps {
  children: ReactNode
  className?: string
  /** Ref to the scrollable list area (for scroll-into-view) */
  listRef?: RefObject<HTMLDivElement | null>
  /** ARIA role, default: "listbox" */
  role?: string
}

export const PickerList = forwardRef<HTMLDivElement, PickerListProps>(
  function PickerList({ children, className, listRef, role = "listbox" }, ref) {
    return (
      <div
        ref={ref ?? listRef}
        role={role}
        className={cn("max-h-[60vh] overflow-y-auto py-1", className)}
      >
        {children}
      </div>
    )
  },
)

// ---------------------------------------------------------------------------
// PickerSearch — search/filter input
// ---------------------------------------------------------------------------

interface PickerSearchProps {
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  placeholder?: string
  inputRef?: RefObject<HTMLInputElement | null>
  /** Show magnifying glass icon (default: true) */
  showIcon?: boolean
  /** ARIA attributes */
  ariaLabel?: string
  /** Extra className for the wrapper */
  className?: string
}

export function PickerSearch({
  value,
  onChange,
  onKeyDown,
  placeholder = "Search…",
  inputRef,
  showIcon = true,
  ariaLabel,
  className,
}: PickerSearchProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 border-b border-border px-3 py-2.5",
        className,
      )}
    >
      {showIcon && (
        <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
      )}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        role="combobox"
        aria-expanded={true}
        aria-autocomplete="list"
        className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// PickerItem — individual row
// ---------------------------------------------------------------------------

interface PickerItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Icon rendered at the leading edge */
  icon?: ReactNode
  /** Whether this item is currently selected */
  selected?: boolean
  /** Show trailing checkmark when selected (default: true) */
  checkmark?: boolean
  /** Content rendered at the trailing edge (badges, indicators) */
  trailing?: ReactNode
  /** Visual variant */
  variant?: "default" | "destructive"
  /** Keyboard highlight (overrides selected bg when active) */
  highlighted?: boolean
  /** Label content */
  children: ReactNode
}

export function PickerItem({
  icon,
  selected,
  checkmark = true,
  trailing,
  variant = "default",
  onClick,
  onMouseEnter,
  highlighted,
  disabled,
  className,
  role = "option",
  children,
  ...rest
}: PickerItemProps) {
  return (
    <button
      type="button"
      role={role}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-3 pointer-fine:gap-2.5 rounded-md px-3 text-left text-base pointer-fine:text-sm transition-colors",
        // Responsive touch targets: py-3 on mobile (48px), py-2 on desktop
        "py-3 pointer-fine:py-2",
        variant === "default" && "hover:bg-accent",
        variant === "destructive" &&
          "text-destructive hover:bg-destructive/10",
        // Highlight takes priority over selected bg
        highlighted
          ? "bg-accent text-accent-foreground"
          : selected && variant === "default" && "bg-primary/10",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...rest}
    >
      {icon && <span className="flex shrink-0 items-center">{icon}</span>}
      <span className="min-w-0 flex-1 truncate font-medium">{children}</span>
      {trailing}
      {checkmark && selected && (
        <CheckmarkIcon className="size-4 shrink-0 text-primary" />
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// PickerHeader — section header
// ---------------------------------------------------------------------------

interface PickerHeaderProps {
  children: ReactNode
  className?: string
}

export function PickerHeader({ children, className }: PickerHeaderProps) {
  return (
    <div className={cn("px-3 py-1.5 pointer-fine:py-1", className)}>
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PickerSeparator — visual divider
// ---------------------------------------------------------------------------

export function PickerSeparator({ className }: { className?: string }) {
  return <div className={cn("my-0.5 h-px bg-border", className)} />
}

// ---------------------------------------------------------------------------
// PickerEmpty — empty state message
// ---------------------------------------------------------------------------

export function PickerEmpty({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "px-3 py-4 text-center text-xs text-muted-foreground",
        className,
      )}
    >
      {children}
    </div>
  )
}
